import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Camera, User, Mail, Shield, Loader2, Check, LogOut, CreditCard, Plus, Trash2, Zap, RefreshCw, Wallet, Clock, ArrowDownLeft, ArrowUpRight, Lock, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { userProfileApi, avatarOptions, paymentsApi } from '../lib/api';
import { toast } from 'sonner';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import StripeCheckoutModal from '../components/StripeCheckoutModal';
import { getPendingPurchase, clearPendingPurchase } from '../components/PaymentMethodModal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// Confirmation popup rendered via portal
const ConfirmPopup = ({ isOpen, onConfirm, onCancel, title, message, confirmText = 'Confirm', confirmVariant = 'destructive', loading = false }) => {
  if (!isOpen) return null;
  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4" onClick={onCancel} data-testid="confirm-popup-overlay">
      <div className="bg-[#0F0B1E] border border-white/10 rounded-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
          <h3 className="text-lg font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }} data-testid="confirm-popup-title">{title}</h3>
          <p className="text-sm text-gray-400 mb-6">{message}</p>
          <div className="flex gap-3">
            <button onClick={onCancel} disabled={loading}
              className="flex-1 py-2.5 rounded-xl border border-white/10 text-gray-300 hover:bg-white/5 font-medium transition-colors disabled:opacity-50"
              data-testid="confirm-popup-cancel">
              Cancel
            </button>
            <button onClick={onConfirm} disabled={loading}
              className={`flex-1 py-2.5 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${
                confirmVariant === 'destructive' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-[#8B5CF6] hover:bg-[#7C3AED] text-white'
              }`}
              data-testid="confirm-popup-confirm">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const ProfilePage = () => {
  const { user, isAuthenticated, logout, refreshUser, isPremium, isOwner } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [editName, setEditName] = useState('');
  const [showAvatarDialog, setShowAvatarDialog] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [subLoading, setSubLoading] = useState(false);
  const [savedCard, setSavedCard] = useState(null);
  const [loadingCard, setLoadingCard] = useState(false);
  const cardRequestInFlight = useRef(false);
  const [userBalance, setUserBalance] = useState(0);
  const [showAddBalance, setShowAddBalance] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const fileInputRef = useRef(null);
  const autoSetupTriggered = useRef(false);

  // Confirmation popup state
  const [confirmPopup, setConfirmPopup] = useState({ open: false, type: null });
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Change password state
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordStep, setPasswordStep] = useState(1); // 1=current, 2=new
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchProfile = async () => {
      try {
        const data = await userProfileApi.get();
        setProfileData(data);
        setEditName(data.name || '');
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        toast.error('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    const fetchSubscription = async () => {
      try {
        const data = await paymentsApi.getSubscriptionStatus();
        setSubscription(data);
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
      }
    };

    const fetchSavedCard = async () => {
      try {
        const data = await paymentsApi.getSavedCard();
        setSavedCard(data.card);
      } catch (error) {
        console.error('Failed to fetch saved card:', error);
      }
    };

    const fetchBalance = async () => {
      try {
        const data = await paymentsApi.getBalance();
        setUserBalance(data.balance || 0);
      } catch (error) {
        console.error('Failed to fetch balance:', error);
      }
    };

    const fetchTransactions = async () => {
      try {
        const data = await paymentsApi.getTransactions();
        setTransactions(data.transactions || []);
      } catch (error) {
        console.error('Failed to fetch transactions:', error);
      }
    };

    fetchProfile();
    fetchSubscription();
    fetchSavedCard();
    fetchBalance();
    fetchTransactions();
  }, [isAuthenticated, navigate]);

  // Auto-trigger card setup when redirected from payment flow
  useEffect(() => {
    if (autoSetupTriggered.current) return;
    const action = searchParams.get('action');
    if (action === 'add_card' && isAuthenticated && !loadingCard) {
      autoSetupTriggered.current = true;
      // Auto-trigger the Add Card flow after a small delay
      const timer = setTimeout(async () => {
        try {
          const cardData = await paymentsApi.getSavedCard();
          if (!cardData.card) {
            // User doesn't have a card, trigger setup
            toast.info('Please add a card to continue your purchase');
            handleAddCard();
          } else {
            // Card already exists, redirect back
            const pending = getPendingPurchase();
            if (pending?.returnTo) {
              clearPendingPurchase();
              // Re-save so the target page can pick it up
              sessionStorage.setItem('reframes_pending_purchase', JSON.stringify({ ...pending, autoOpen: true, timestamp: Date.now() }));
              navigate(pending.returnTo);
            }
          }
        } catch { /* ignore */ }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [searchParams, isAuthenticated, loadingCard]);

  const handleSaveName = async () => {
    if (!editName.trim()) {
      toast.error('Please enter a name');
      return;
    }

    if (editName.trim() === profileData?.name) {
      return; // No change
    }

    setSaving(true);
    try {
      const updated = await userProfileApi.update({ name: editName.trim() });
      setProfileData(updated);
      toast.success('Name updated');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.message || 'Failed to update name');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image too large. Maximum size: 2MB');
      return;
    }

    setUploadingAvatar(true);
    try {
      const result = await userProfileApi.uploadAvatar(file);
      setProfileData(prev => ({ ...prev, picture: result.url }));
      toast.success('Profile picture updated');
      setShowAvatarDialog(false);
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.message || 'Failed to upload image');
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  };

  const handleSelectPresetAvatar = async (avatarUrl) => {
    setSaving(true);
    try {
      const updated = await userProfileApi.update({ picture: avatarUrl });
      setProfileData(updated);
      toast.success('Profile picture updated');
      setShowAvatarDialog(false);
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.message || 'Failed to update picture');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleRemoveSavedCard = async () => {
    setConfirmLoading(true);
    try {
      const result = await paymentsApi.removeCard();
      setSavedCard(null);
      if (result.has_active_subscription) {
        toast.warning('Carta rimossa. Attenzione: hai un abbonamento attivo! Aggiungi un nuovo metodo di pagamento prima del rinnovo.');
      } else {
        toast.success('Saved card removed');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to remove card');
    } finally {
      setConfirmLoading(false);
      setConfirmPopup({ open: false, type: null });
    }
  };

  const handleReplaceCard = async () => {
    if (cardRequestInFlight.current) return;
    cardRequestInFlight.current = true;
    setConfirmLoading(true);
    setConfirmPopup({ open: false, type: null });
    setLoadingCard(true);
    try {
      const result = await paymentsApi.replaceCard(window.location.origin);
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
        return;
      }
      toast.error('No redirect URL received from Stripe');
    } catch (error) {
      console.error('[Stripe] Replace card failed:', error);
      // Auto-fix: if Stripe customer is stale, fix it and retry
      try {
        await paymentsApi.fixCustomer();
        const result = await paymentsApi.replaceCard(window.location.origin);
        if (result.checkout_url) {
          window.location.href = result.checkout_url;
          return;
        }
      } catch (retryError) {
        console.error('[Stripe] Retry also failed:', retryError);
        const msg = retryError?.detail || retryError?.message || 'Errore Stripe. Riprova tra qualche minuto.';
        toast.error(msg);
      }
    }
    cardRequestInFlight.current = false;
    setLoadingCard(false);
    setConfirmLoading(false);
  };

  const handleVerifyCurrentPassword = async () => {
    if (!currentPassword) { setPasswordError('Enter your current password'); return; }
    setPasswordLoading(true);
    setPasswordError('');
    try {
      // Validate current password by calling change-password with same password
      // Instead, we'll make a lightweight validation by trying a login
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: profileData?.email, password: currentPassword }),
      });
      if (!response.ok) {
        setPasswordError('Wrong current password');
        setPasswordLoading(false);
        return;
      }
      setPasswordStep(2);
    } catch {
      setPasswordError('Verification failed. Try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    if (!newPassword || !confirmPassword) { setPasswordError('Fill in both fields'); return; }
    if (newPassword !== confirmPassword) { setPasswordError('Passwords do not match'); return; }
    if (newPassword.length < 6) { setPasswordError('Password must be at least 6 characters'); return; }

    setPasswordLoading(true);
    try {
      await userProfileApi.changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully');
      // Reset all state
      setShowPasswordSection(false);
      setPasswordStep(1);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
    } catch (error) {
      setPasswordError(error.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleAddCard = async () => {
    if (cardRequestInFlight.current) return;
    cardRequestInFlight.current = true;
    setLoadingCard(true);
    try {
      const result = await paymentsApi.replaceCard(window.location.origin);
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
        return;
      }
      toast.error('No redirect URL received from Stripe');
    } catch (error) {
      console.error('[Stripe] Add card failed:', error);
      // Auto-fix: if Stripe customer is stale, fix it and retry
      try {
        await paymentsApi.fixCustomer();
        const result = await paymentsApi.replaceCard(window.location.origin);
        if (result.checkout_url) {
          window.location.href = result.checkout_url;
          return;
        }
      } catch (retryError) {
        console.error('[Stripe] Retry also failed:', retryError);
        const msg = retryError?.detail || retryError?.message || 'Errore Stripe. Riprova tra qualche minuto.';
        toast.error(msg);
      }
    }
    cardRequestInFlight.current = false;
    setLoadingCard(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030014] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  const currentPicture = profileData?.picture?.startsWith('/') 
    ? `${window.location.origin}${profileData.picture}` 
    : profileData?.picture || avatarOptions[0];

  return (
    <div className="min-h-screen bg-[#030014]" data-testid="profile-page">
      <Navbar />

      <div className="pt-24 pb-12 max-w-2xl mx-auto px-4 md:px-8">
        {/* Profile Header */}
        <div className="text-center mb-8">
          <h1
            className="text-3xl font-bold mb-2"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            My Profile
          </h1>
          <p className="text-gray-400">Manage your account settings</p>
        </div>

        {/* Profile Card */}
        <div className="admin-card p-8">
          {/* Avatar Section */}
          <div className="flex flex-col items-center mb-8">
            <div className="relative group">
              <img
                src={currentPicture}
                alt={profileData?.name}
                className="w-32 h-32 rounded-full object-cover ring-4 ring-[#8B5CF6]/30"
                data-testid="profile-avatar"
              />
              <button
                onClick={() => setShowAvatarDialog(true)}
                className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                data-testid="change-avatar-btn"
              >
                <Camera className="w-8 h-8 text-white" />
              </button>
            </div>
            <button
              onClick={() => setShowAvatarDialog(true)}
              className="mt-3 text-sm text-[#8B5CF6] hover:text-[#8B5CF6]/80 transition-colors"
            >
              Change profile picture
            </button>
          </div>

          {/* Profile Info */}
          <div className="space-y-6">
            {/* Name Field */}
            <div>
              <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                <User className="w-4 h-4" />
                Display Name
              </label>
              <div className="flex gap-3">
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Enter your name"
                  className="bg-[#030014] border-[#8B5CF6]/20 flex-1"
                  data-testid="profile-name-input"
                />
                <Button
                  onClick={handleSaveName}
                  disabled={saving || editName.trim() === profileData?.name}
                  className="bg-[#8B5CF6] hover:bg-[#8B5CF6]/80"
                  data-testid="save-name-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Email Field (Read-only) */}
            <div>
              <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                <Mail className="w-4 h-4" />
                Email Address
              </label>
              <Input
                value={profileData?.email || ''}
                disabled
                className="bg-[#030014]/50 border-[#8B5CF6]/10 text-gray-500"
                data-testid="profile-email"
              />
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>

            {/* Role Badge */}
            {profileData?.role && (
              <div>
                <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                  <Shield className="w-4 h-4" />
                  Account Type
                </label>
                <div className="flex items-center gap-2">
                  <span className={`badge ${
                    profileData.role === 'owner' 
                      ? 'bg-[#00FF9D]/20 text-[#00FF9D]' 
                      : profileData.role === 'company'
                        ? 'bg-[#8B5CF6]/20 text-[#8B5CF6]'
                        : 'bg-gray-500/20 text-gray-400'
                  }`}>
                    {profileData.role === 'owner' ? 'Platform Owner' : 
                     profileData.role === 'company' ? 'Creator' : 'User'}
                  </span>
                  {profileData.is_admin && (
                    <span className="badge bg-yellow-500/20 text-yellow-400">Admin</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-white/10 my-8" />

          {/* Subscription Section */}
          <div className="mb-8" data-testid="subscription-section">
            <h3 className="flex items-center gap-2 text-lg font-semibold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <Zap className="w-5 h-5 text-[#8B5CF6]" />
              Subscription
            </h3>
            
            {(subscription?.active || isPremium || isOwner) ? (
              <div className="p-4 rounded-lg bg-[#8B5CF6]/10 border border-[#8B5CF6]/30">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <span className="inline-block px-2 py-0.5 text-xs rounded bg-[#8B5CF6] text-white mb-2">
                      {subscription?.cancel_at_period_end ? 'Cancelling' : 'Premium Active'}
                    </span>
                    <p className="text-sm text-gray-400">
                      {isOwner ? 'As a platform owner, you have premium access.' : 'Enjoy ad-free viewing!'}
                    </p>
                    {subscription?.current_period_end && (
                      <p className="text-xs text-gray-500 mt-1">
                        {subscription.cancel_at_period_end
                          ? `Active until: ${new Date(subscription.current_period_end).toLocaleDateString('en-US')}`
                          : `Next renewal: ${new Date(subscription.current_period_end).toLocaleDateString('en-US')}`
                        }
                      </p>
                    )}
                    {subscription?.cancel_at_period_end && (
                      <p className="text-xs text-yellow-400 mt-1">
                        Your subscription will not renew after this period.
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Zap className="w-8 h-8 text-[#8B5CF6]" />
                    {subscription?.active && !isOwner && !subscription?.cancel_at_period_end && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button
                            disabled={subLoading}
                            className="text-xs text-red-400 hover:text-red-300 underline transition-colors disabled:opacity-50"
                            data-testid="cancel-subscription-btn"
                          >
                            {subLoading ? 'Cancelling...' : 'Cancel subscription'}
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#0F0B1E] border border-white/10">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-white">Cancel subscription?</AlertDialogTitle>
                            <AlertDialogDescription className="text-gray-400">
                              Your premium access will remain active until <span className="text-white font-medium">{subscription?.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString('en-US') : 'the end of the billing period'}</span>. After that, you will lose access to premium content.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-transparent border border-white/10 text-gray-300 hover:bg-white/5 hover:text-white" data-testid="cancel-sub-dismiss">
                              Keep subscription
                            </AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-red-600 hover:bg-red-700 text-white"
                              data-testid="cancel-sub-confirm"
                              onClick={async () => {
                                setSubLoading(true);
                                try {
                                  const res = await paymentsApi.cancelSubscription();
                                  toast.success(res.message || 'Subscription cancelled');
                                  const updated = await paymentsApi.getSubscriptionStatus();
                                  setSubscription(updated);
                                } catch (err) {
                                  toast.error(err.message || 'Failed to cancel');
                                } finally {
                                  setSubLoading(false);
                                }
                              }}
                            >
                              Yes, cancel
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-lg bg-gray-800/50 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="inline-block px-2 py-0.5 text-xs rounded bg-gray-600 text-gray-300 mb-2">Free Plan</span>
                    <p className="text-sm text-gray-400">You're on the free plan with ads.</p>
                  </div>
                  <Link 
                    to="/pricing"
                    className="px-4 py-2 rounded-lg bg-[#8B5CF6] hover:bg-[#8B5CF6]/80 text-white text-sm font-medium transition-colors"
                    data-testid="upgrade-subscription-btn"
                  >
                    Go Premium
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Add Balance Section */}
          <div className="mb-8" data-testid="add-balance-section">
            <h3 className="flex items-center gap-2 text-lg font-semibold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <Wallet className="w-5 h-5 text-[#6EE7A8]" />
              Balance
            </h3>
            <div className="p-5 rounded-xl bg-gradient-to-r from-[#6EE7A8]/5 to-[#0F0B1E] border border-[#6EE7A8]/15">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Current Balance</p>
                  <p className="text-3xl font-bold text-[#6EE7A8]" data-testid="user-balance">
                    {userBalance.toFixed(2)} <span className="text-base text-gray-400 font-normal">USD</span>
                  </p>
                </div>
                <Button
                  onClick={() => setShowAddBalance(true)}
                  className="bg-[#6EE7A8]/15 hover:bg-[#6EE7A8]/25 text-[#6EE7A8] border border-[#6EE7A8]/30 font-semibold"
                  data-testid="add-balance-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Balance
                </Button>
              </div>
            </div>
          </div>

          {/* Saved Card Section */}
          <div className="mb-8" data-testid="saved-card-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="flex items-center gap-2 text-lg font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                <CreditCard className="w-5 h-5 text-[#8B5CF6]" />
                Saved Card
              </h3>
              {savedCard ? (
                <div className="flex gap-2">
                  <Button
                    onClick={() => setConfirmPopup({ open: true, type: 'replace' })}
                    disabled={loadingCard}
                    size="sm"
                    className="bg-white/10 hover:bg-white/15"
                    data-testid="replace-card-btn"
                  >
                    {loadingCard ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                    {loadingCard ? 'Redirecting...' : 'Replace'}
                  </Button>
                  <Button
                    onClick={() => setConfirmPopup({ open: true, type: 'remove' })}
                    disabled={loadingCard}
                    size="sm"
                    variant="destructive"
                    data-testid="remove-card-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                    Remove
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={handleAddCard}
                  disabled={loadingCard}
                  size="sm"
                  className="bg-[#8B5CF6] hover:bg-[#8B5CF6]/80"
                  data-testid="add-card-btn"
                >
                  {loadingCard ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                  {loadingCard ? 'Connecting to Stripe...' : 'Add Card'}
                </Button>
              )}
            </div>
            {savedCard ? (
              <div className="p-4 rounded-xl bg-gradient-to-r from-[#8B5CF6]/10 to-[#0F0B1E] border border-[#8B5CF6]/20">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-8 rounded bg-white/10 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-[#8B5CF6]" />
                  </div>
                  <div>
                    <p className="font-medium text-sm" data-testid="saved-card-info">
                      {savedCard.brand} ending in {savedCard.last4}
                    </p>
                    <p className="text-xs text-gray-500">
                      Saved {savedCard.saved_at ? new Date(savedCard.saved_at).toLocaleDateString('en-US') : ''}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 rounded-lg border border-dashed border-white/20 text-center">
                <CreditCard className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                <p className="text-gray-400 text-sm">No saved card</p>
                <p className="text-gray-500 text-xs mt-1">Add a card for faster donations and purchases</p>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-white/10 my-8" />

          {/* Transaction History */}
          <div className="mb-8" data-testid="transaction-history-section">
            <h3 className="flex items-center gap-2 text-lg font-semibold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <Clock className="w-5 h-5 text-gray-400" />
              Payment History
            </h3>
            {transactions.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {transactions.slice(0, 10).map((txn, i) => {
                  const isTopup = txn.payment_type === 'add_balance';
                  const isPayout = txn.payment_type === 'payout';
                  return (
                    <div key={txn.transaction_id || i}
                      className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/5"
                      data-testid={`txn-${txn.transaction_id || i}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isTopup ? 'bg-[#6EE7A8]/10' : isPayout ? 'bg-red-500/10' : 'bg-[#8B5CF6]/10'
                      }`}>
                        {isTopup ? <ArrowDownLeft className="w-4 h-4 text-[#6EE7A8]" /> :
                         isPayout ? <ArrowUpRight className="w-4 h-4 text-red-400" /> :
                         <CreditCard className="w-4 h-4 text-[#8B5CF6]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium capitalize truncate">
                          {txn.payment_type?.replace(/_/g, ' ') || 'Payment'}
                        </p>
                        <p className="text-[11px] text-gray-500">
                          {txn.created_at ? new Date(txn.created_at).toLocaleDateString('en-GB', { 
                            day: '2-digit', month: 'short', year: 'numeric' 
                          }) : ''}
                          {txn.payment_method && ` · ${txn.payment_method}`}
                        </p>
                      </div>
                      <span className={`text-sm font-bold tabular-nums ${isTopup ? 'text-[#6EE7A8]' : 'text-white'}`}>
                        {isTopup ? '+' : ''}{txn.amount?.toFixed(2)} USD
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-6 rounded-lg border border-dashed border-white/10 text-center">
                <Clock className="w-8 h-8 mx-auto mb-2 text-gray-600" />
                <p className="text-sm text-gray-500">No payment history yet</p>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-white/10 my-8" />

          {/* Change Password Section */}
          <div className="mb-8" data-testid="change-password-section">
            <h3 className="flex items-center gap-2 text-lg font-semibold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <Lock className="w-5 h-5 text-gray-400" />
              Password
            </h3>

            {!showPasswordSection ? (
              <Button
                onClick={() => { setShowPasswordSection(true); setPasswordStep(1); setPasswordError(''); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }}
                variant="outline"
                className="border-white/10 hover:bg-white/5"
                data-testid="change-password-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            ) : (
              <div className="p-5 rounded-xl bg-white/[0.02] border border-white/10 space-y-4">
                {passwordStep === 1 ? (
                  <>
                    <div>
                      <label className="text-sm text-gray-400 mb-1.5 block">Current Password</label>
                      <div className="relative">
                        <Input
                          type={showCurrentPw ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={e => { setCurrentPassword(e.target.value); setPasswordError(''); }}
                          placeholder="Enter your current password"
                          className="bg-[#030014] border-white/10 pr-10"
                          data-testid="current-password-input"
                          onKeyDown={e => e.key === 'Enter' && handleVerifyCurrentPassword()}
                        />
                        <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                          data-testid="toggle-current-pw">
                          {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    {passwordError && <p className="text-sm text-red-400" data-testid="password-error">{passwordError}</p>}
                    <div className="flex gap-3">
                      <Button onClick={() => setShowPasswordSection(false)} variant="outline" className="border-white/10" data-testid="cancel-password-btn">
                        Cancel
                      </Button>
                      <Button onClick={handleVerifyCurrentPassword} disabled={passwordLoading || !currentPassword}
                        className="bg-[#8B5CF6] hover:bg-[#7C3AED]" data-testid="verify-password-btn">
                        {passwordLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Continue
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="text-sm text-gray-400 mb-1.5 block">New Password</label>
                      <div className="relative">
                        <Input
                          type={showNewPw ? 'text' : 'password'}
                          value={newPassword}
                          onChange={e => { setNewPassword(e.target.value); setPasswordError(''); }}
                          placeholder="Enter new password (min 6 chars)"
                          className="bg-[#030014] border-white/10 pr-10"
                          data-testid="new-password-input"
                        />
                        <button type="button" onClick={() => setShowNewPw(!showNewPw)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                          {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 mb-1.5 block">Confirm New Password</label>
                      <div className="relative">
                        <Input
                          type={showConfirmPw ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={e => { setConfirmPassword(e.target.value); setPasswordError(''); }}
                          placeholder="Confirm new password"
                          className="bg-[#030014] border-white/10 pr-10"
                          data-testid="confirm-password-input"
                          onKeyDown={e => e.key === 'Enter' && handleChangePassword()}
                        />
                        <button type="button" onClick={() => setShowConfirmPw(!showConfirmPw)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                          {showConfirmPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    {passwordError && <p className="text-sm text-red-400" data-testid="password-error">{passwordError}</p>}
                    <div className="flex gap-3">
                      <Button onClick={() => { setPasswordStep(1); setPasswordError(''); }} variant="outline" className="border-white/10" data-testid="back-password-btn">
                        Back
                      </Button>
                      <Button onClick={handleChangePassword} disabled={passwordLoading || !newPassword || !confirmPassword}
                        className="bg-[#8B5CF6] hover:bg-[#7C3AED]" data-testid="submit-password-btn">
                        {passwordLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Change Password
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-white/10 my-8" />

          {/* Sign Out */}
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </div>

      {/* Avatar Selection Dialog */}
      <Dialog open={showAvatarDialog} onOpenChange={setShowAvatarDialog}>
        <DialogContent className="bg-[#0F0B1E] border-[#8B5CF6]/30 max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Change Profile Picture
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 pt-4">
            {/* Upload Custom Image */}
            <div>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={handleAvatarUpload}
                data-testid="avatar-file-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingAvatar}
                variant="outline"
                className="w-full border-dashed border-[#8B5CF6]/30 hover:border-[#8B5CF6]"
                data-testid="upload-avatar-btn"
              >
                {uploadingAvatar ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Camera className="w-4 h-4 mr-2" />
                    Upload Custom Image
                  </>
                )}
              </Button>
              <p className="text-xs text-gray-500 text-center mt-2">
                JPEG, PNG, WebP or GIF. Max 2MB.
              </p>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 border-t border-white/10" />
              <span className="text-sm text-gray-500">or choose an avatar</span>
              <div className="flex-1 border-t border-white/10" />
            </div>

            {/* Preset Avatars */}
            <div className="grid grid-cols-4 gap-3">
              {avatarOptions.map((avatar, index) => (
                <button
                  key={index}
                  onClick={() => handleSelectPresetAvatar(avatar)}
                  disabled={saving}
                  className={`relative rounded-lg overflow-hidden transition-all hover:ring-2 hover:ring-[#8B5CF6] hover:scale-105 ${
                    currentPicture === avatar ? 'ring-2 ring-[#8B5CF6]' : ''
                  }`}
                  data-testid={`preset-avatar-${index}`}
                >
                  <img 
                    src={avatar} 
                    alt={`Avatar ${index + 1}`} 
                    className="w-full aspect-square object-cover" 
                  />
                  {currentPicture === avatar && (
                    <div className="absolute inset-0 bg-[#8B5CF6]/30 flex items-center justify-center">
                      <Check className="w-6 h-6 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Balance Modal */}
      <StripeCheckoutModal
        isOpen={showAddBalance}
        onClose={() => setShowAddBalance(false)}
        paymentType="add_balance"
        onSuccess={async () => {
          try {
            const [balData, txnData] = await Promise.all([
              paymentsApi.getBalance(),
              paymentsApi.getTransactions(),
            ]);
            setUserBalance(balData.balance || 0);
            setTransactions(txnData.transactions || []);
          } catch { /* ignore */ }
        }}
      />

      {/* Remove Card Confirmation */}
      <ConfirmPopup
        isOpen={confirmPopup.open && confirmPopup.type === 'remove'}
        onCancel={() => setConfirmPopup({ open: false, type: null })}
        onConfirm={handleRemoveSavedCard}
        title="Remove Saved Card"
        message={savedCard
          ? (subscription?.active
            ? `Attenzione: hai un abbonamento Premium attivo! Rimuovendo la carta ${savedCard.brand} •••• ${savedCard.last4}, il prossimo rinnovo potrebbe fallire e l'abbonamento verrà sospeso. Vuoi procedere?`
            : `Are you sure you want to remove your ${savedCard.brand} card ending in ${savedCard.last4}? You won't be able to make quick payments until you add a new card.`)
          : 'Are you sure you want to remove this card?'}
        confirmText="Remove Card"
        confirmVariant="destructive"
        loading={confirmLoading}
      />

      {/* Replace Card Confirmation */}
      <ConfirmPopup
        isOpen={confirmPopup.open && confirmPopup.type === 'replace'}
        onCancel={() => setConfirmPopup({ open: false, type: null })}
        onConfirm={handleReplaceCard}
        title="Replace Saved Card"
        message={savedCard ? `Are you sure that you want to change your card? Your current ${savedCard.brand} card ending in ${savedCard.last4} will be replaced.` : 'Are you sure that you want to change your card?'}
        confirmText="Continue"
        confirmVariant="primary"
        loading={confirmLoading}
      />

    </div>
  );
};

export default ProfilePage;
